import { Button, Checkbox, CheckboxOptionType, Spin, Alert } from "antd";

import { useMemo, useContext, useEffect, useState, useCallback } from "react";
import { AppContext } from "../../context/app-context";
import { CountrySelect } from "../../components";
import { ContentApi } from "../../apis/content-api";

import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { downloadBlob } from "../../utils/api";
import { faFileExport } from "@fortawesome/pro-regular-svg-icons";
import { ExportApi } from "../../apis/export-api";

import "./ContentExport.scss";

const labelMap: Record<string, Record<string, string>> = {
  AL: {
    "Additional (New 2023 content)": "Info LGBTI+",
  },
  CS: {
    "Additional (New 2023 content)": "Guide to Czech Laws",
    "Dispelling Myths": "Myths",
    "Sexual Consent": "Sexual Violence",
  },
  GR: {
    "Additional (New 2023 content)": "LGBTQIA+ Support",
  },
  HU: {
    "Additional (New 2023 content)": "Warning Signs",
  },
  IE: {
    "Additional (New 2023 content)": "LGBTQIA+ Support",
  },
  IT: {
    "Additional (New 2023 content)": "Financial Independence",
  },
  LU: {
    "Additional (New 2023 content)": "Legal Guide",
  },
  PT: {
    "Additional (New 2023 content)": "LGBTQ+ Support",
  },
  RO: {
    "Additional (New 2023 content)": "Human Trafficking",
  },
  SA: {
    "Leaving a Relationship": "Safety Planning",
    "Sexual Consent": "Sexual Offences",
    "Stalking and Harassment": "Domestic Violence",
    "Additional (New 2023 content)": "How Law can Help",
  },
  US: {
    "Additional (New 2023 content)": "Helpful Videos",
  },
}

const getLabel = (countryCode: string, label: string): string => {
  return labelMap[countryCode]?.[label] || label;
}

const ContentExport = () => {
  const { isLoadingCountry, selectedCountry, profile } = useContext(AppContext);

  const [ languageOptions, setLanguageOptions] = useState<CheckboxOptionType[]>([]);
  const [ selectedLanguages, setSelectedLanguages] = useState<string[]>([]);

  const [contentGroupOptions, setContentGroupOptions] = useState<CheckboxOptionType[]>([]);
  const [ selectedContentGroups, setSelectedContentGroups] = useState<string[]>([]);

  const [serviceTypeOptions, setServiceTypeOptions] = useState<CheckboxOptionType[]>([]);
  const [ selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);

  const [ isLoadingContentOptions, setLoadingContentOptions] = useState<boolean>(true);
  const [ , setLoadingServiceTypes] = useState<boolean>(false);

  const [ isExportingDocs, setExportingDocs] = useState<boolean>(false);
  const [ exportErrorMsg, setExportErrorMsg ] = useState<string | null>(null);

  useEffect(() => {
    ContentApi.getContentTypes().then((data) => {
	  setContentGroupOptions(data.map(x => { return { label: x, value: x}; }));
	  setLoadingContentOptions(false);
	});
  }, []);

  useEffect(() => {
	if (profile?.ExportServicesEnabled) {
      setLoadingServiceTypes(true);
	  ContentApi.getServiceTypes().then((data) => {
	    setServiceTypeOptions(data.map(x => { return { label: x, value: x }; }));
		setLoadingServiceTypes(false);
	  });
	}
  }, [profile]);
  
  const filteredContentGroupOptions = useMemo(() => {
    if (selectedCountry === null) return contentGroupOptions;
    return contentGroupOptions.map((group: any) => {
      return {
        value: group.value,
        label: getLabel(selectedCountry.code.iv, group.label)
      }
    })
  }, [contentGroupOptions, selectedCountry]);

  const filteredServiceTypeOptions = useMemo(() => {
	if (!selectedCountry || selectedCountry.code.iv !== "US") {
	  return serviceTypeOptions;
	}

	// For US, filter out locations from export options
	return serviceTypeOptions
	  .filter(x => x.value !== "Locations");
  }, [serviceTypeOptions, selectedCountry]);

  const loadCountries = useCallback(() => {
    // setLoading(true);
	// httpPost(`Directory/Locations`, {country: c, isNationalHelplines: isNationalHelplines}, token, (d: any[]) => {
	//     setCountries(d);
	//     setLoading(false);
	// });'
		
	if (selectedCountry !== null) {
	  setLanguageOptions(selectedCountry.languages.iv.map((e) => {
		return {
		  label: e.language,
		  value: e.code,
		};
	  }));
	}

	setSelectedLanguages([]);
	setSelectedContentGroups(selectedContentGroups.filter(x => filteredContentGroupOptions.map(x => x.value).includes(x)));
	setSelectedServiceTypes(selectedServiceTypes.filter(x => filteredServiceTypeOptions.map(x => x.value).includes(x)));
  }, [selectedContentGroups, selectedCountry, filteredContentGroupOptions, selectedServiceTypes, filteredServiceTypeOptions]);

  useEffect(() => {
	loadCountries();
	// should only reload once -- per selected country change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const editLangs = (value: CheckboxValueType[]) => {
	setSelectedLanguages(value as string[]);
  }

  const allLangs = () => {
    if (selectedLanguages.length !== languageOptions.length) {
	  setSelectedLanguages(languageOptions?.map(x => x.value as string));
	} else {
	  setSelectedLanguages([]);
	}	
  }

  const editContentGroups = (value: CheckboxValueType[]) => {
	setSelectedContentGroups(value as string[]);
  }

  const allContentGroups = () => {
	if (selectedContentGroups.length !== filteredContentGroupOptions.length) {
	  setSelectedContentGroups(filteredContentGroupOptions.map(x => x.value as string));
	} else {
	  setSelectedContentGroups([]);
	}
  }

  const editServiceTypes = (value: CheckboxValueType[]) => {
	setSelectedServiceTypes(value as string[]);
  }

  const allServiceTypes = () => {
	if (selectedServiceTypes.length !== filteredServiceTypeOptions.length) {
	  setSelectedServiceTypes(filteredServiceTypeOptions.map(x => x.value as string));
	} else {
	  setSelectedServiceTypes([]);
	}
  }

  return (
    <>
	  <div className="content-header">
	    <h2>{"Content Export"}</h2>
		<span className="row-right"></span>
		{ profile?.Market === "All" && (
		  <>
		    <span className="row-right">{"Country / Market:"}</span>
			<CountrySelect />
		  </>
	    )}
		<Button
		  type="primary"
		  icon={<FontAwesomeIcon icon={faFileExport} />}
		  disabled={
		    isLoadingCountry || isLoadingContentOptions 
			|| !selectedCountry 
			|| selectedLanguages.length === 0 
			|| (selectedContentGroups.length === 0 && selectedServiceTypes.length === 0)
		  }
		  title={
		    selectedLanguages.length === 0 ? "Please select at least one language."
		    : (selectedContentGroups.length === 0 && selectedServiceTypes.length === 0 ? "Please select at least one content group." 
			: undefined)
		  }
		  onClick={() => {
			if (selectedCountry) {
			  setExportingDocs(true);

			  ExportApi.ProcessBulkExport(selectedCountry.code.iv, selectedLanguages, selectedContentGroups.concat(selectedServiceTypes))
			    .then((result) => {

				  // handle error status codes for errors encountered after response could be changed
				  const tempResponse = new Uint8Array(result.data);
				  var dec = new TextDecoder("utf-8");
				  var responseStr = dec.decode(tempResponse);
				  if (responseStr.toLowerCase().includes("statuscode:400" || "statuscode:404" || "statuscode:500")) {
					throw Error();
				  }

				  // if no error status code detected, try to download blob
				  downloadBlob(result);
				})
				.catch(() => {
				  setExportErrorMsg("This content export could not be processed.");
				  setTimeout(() => {
					setExportErrorMsg(null);
				  }, 5000);
				})
				.finally(() => setExportingDocs(false));
			}
		  }}
		  loading={isExportingDocs}
		>
		  {"Export Documents"}
		</Button>
	  </div>
	  { exportErrorMsg && (
		<Alert className="export-error" message={exportErrorMsg} type="error" showIcon />
	  )}
	  <div className="content-body">
		<div className="content-tile content-export-tile">			
		  <div>

		    <div className="row">
			  <h3>{"Language:"}</h3>
			  <Button className="row-right" onClick={allLangs}>
			    {selectedLanguages.length !== languageOptions.length ? "All Languages" : "No Languages"}
		      </Button>
			</div>

			{ !isLoadingCountry ? 
			  <Checkbox.Group
			    onChange={editLangs}
				options={languageOptions}
				value={selectedLanguages}
			  /> 
			  : 
			  <Spin />
		    }
		  </div>
		  

		  <div>
		    <div className="row">
			  <h3>{"Content Group:"}</h3>
			  <Button className="row-right" onClick={allContentGroups}>
			    {selectedContentGroups.length !== filteredContentGroupOptions.length ? "All Content Groups" : "No Content Groups"}
		      </Button>
			</div>
			{ !isLoadingContentOptions ? 
		      <Checkbox.Group
			    onChange={editContentGroups}
				value={selectedContentGroups}
				options={filteredContentGroupOptions}
			  /> 
			  : 
			  <Spin />
		    }
		  </div>

		  { profile?.ExportServicesEnabled === true && (
  		    <div>
			  <div className="row">
			    <h3>{"Service Directory:"}</h3>
			    <Button className="row-right" onClick={allServiceTypes}>
			      {selectedServiceTypes.length !== filteredServiceTypeOptions.length ? "All Service Types" : "No Service Types"}
			    </Button>
			  </div>
			  <Checkbox.Group
		        onChange={editServiceTypes}
		        value={selectedServiceTypes}
			    options={filteredServiceTypeOptions}
		      />
		    </div>
		  )}
		</div>
	  </div>
	</>
  )
}

export default ContentExport;
